import {
  AddIcon,
  BagIcon,
  Box,
  Button,
  CheckIcon,
  Flex,
} from '@iheartjane/reefer';
import type {
  ProductCardCtaIcon,
  ProductCardCtaStyle,
  ProductCardSize,
} from '@iheartjane/types';
import type { ReactNode } from 'react';

import styles from './addToBagButton.module.css';

function getAddToBagLabelText({
  atMaxQuantity,
  hasMultipleWeights,
}: {
  atMaxQuantity: boolean;
  hasMultipleWeights: boolean;
}): string {
  if (atMaxQuantity) return 'Limit reached';
  return hasMultipleWeights ? 'Select weight' : 'Add to bag';
}

function AddToBagLabel({
  atMaxQuantity,
  cartCount,
  hasMultipleWeights,
  isAddingToCart,
}: {
  atMaxQuantity: boolean;
  cartCount: number;
  hasMultipleWeights: boolean;
  isAddingToCart: boolean;
}) {
  if (isAddingToCart) return null; // the button shows loading state

  return (
    <>
      {getAddToBagLabelText({ atMaxQuantity, hasMultipleWeights })}
      {cartCount ? (
        <Box className={styles['count']} height={24} ml={8} width={24}>
          {cartCount}
        </Box>
      ) : null}
    </>
  );
}

function CtaIcon({
  ctaIcon,
  isAddingToCart,
}: {
  ctaIcon: ProductCardCtaIcon;
  isAddingToCart: boolean;
}) {
  if (isAddingToCart) return <CheckIcon color="text-inverse" />;
  return ctaIcon === 'bag' ? (
    <BagIcon color="text-inverse" />
  ) : (
    <AddIcon color="text-inverse" />
  );
}

export interface AddToBagButtonProps {
  atMaxQuantity?: boolean;
  /** Drives the responsive size of the icon-style CTA. */
  cardSize?: ProductCardSize;
  cartCount?: number;
  /** Ignored for card_bottom_full ctaStyle. */
  ctaIcon?: ProductCardCtaIcon;
  ctaStyle?: ProductCardCtaStyle;
  hasMultipleWeights?: boolean;
  isAddingToCart?: boolean;
  /**
   * Single-weight add-to-bag handler. Used by the default button; ignored when
   * {@link renderAddToBag} is provided.
   */
  onAddToBag?: () => void;
  onSelectWeight?: () => void;
  /** Escape hatch for SSR form submission. */
  renderAddToBag?: (button: ReactNode) => ReactNode;
}

export function AddToBagButton({
  atMaxQuantity = false,
  cardSize = 'medium',
  cartCount = 0,
  ctaIcon = 'add',
  ctaStyle = 'card_bottom_full',
  hasMultipleWeights = false,
  isAddingToCart = false,
  onAddToBag,
  onSelectWeight,
  renderAddToBag,
}: AddToBagButtonProps) {
  const disabled = isAddingToCart || atMaxQuantity;
  const usesRenderAddToBag = !hasMultipleWeights && renderAddToBag;
  const clickHandler = hasMultipleWeights ? onSelectWeight : onAddToBag;

  // Icon-style CTA (card_bottom, image_top, image_bottom)
  if (ctaStyle !== 'card_bottom_full') {
    const ariaLabel = getAddToBagLabelText({
      atMaxQuantity,
      hasMultipleWeights,
    });
    const icon = <CtaIcon ctaIcon={ctaIcon} isAddingToCart={isAddingToCart} />;
    const button = (
      <Flex
        position="relative"
        className={styles[`iconCtaWrapper_${cardSize}`]}
      >
        <Button.Icon
          variant="primary"
          icon={icon}
          disabled={disabled}
          aria-label={ariaLabel}
          onClick={usesRenderAddToBag ? undefined : clickHandler}
          type={usesRenderAddToBag ? 'submit' : undefined}
        />
        {cartCount > 0 && (
          <Flex
            className={styles['iconCount']}
            position="absolute"
            top={-2}
            right={-2}
            height={20}
            width={20}
            justifyContent="center"
            alignItems="center"
          >
            {cartCount}
          </Flex>
        )}
      </Flex>
    );

    return usesRenderAddToBag ? <>{renderAddToBag(button)}</> : button;
  }

  // Full-width CTA (card_bottom_full)
  const label = (
    <AddToBagLabel
      atMaxQuantity={atMaxQuantity}
      cartCount={cartCount}
      hasMultipleWeights={hasMultipleWeights}
      isAddingToCart={isAddingToCart}
    />
  );

  const button = (
    <Button
      variant="primary"
      full
      disabled={disabled}
      startIcon={
        isAddingToCart && <CheckIcon className={styles['textContrastColor']} />
      }
      label={label}
      className={styles['addToBagButton']}
      onClick={usesRenderAddToBag ? undefined : clickHandler}
      type={usesRenderAddToBag ? 'submit' : undefined}
    />
  );

  return usesRenderAddToBag ? <>{renderAddToBag(button)}</> : button;
}
